/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { useState } from "react"
import { SectionGrid, Flex } from "frontend/src/components/Layout"
import Carousel from "nuka-carousel"
import { FiChevronLeft, FiChevronRight } from "react-icons/fi"
import Img from "gatsby-image"
import { getFluidFromAsset } from "frontend/src/utils/gatsbyImageHelpers"
import { Icon } from "frontend/src/components/Icon"
import { CMSLink } from "frontend/src/components/CMSLink"
import "./verticalAlignment.css"

const buttonStyles = {
  cursor: "pointer",
  background: "#FFFFFF",
  border: "none",
  width: "55px",
  height: "55px",
  py: 4,
  px: 4,
  borderRadius: "50%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  "&:active": {
    outline: "none",
  },
  "&:focus": {
    outline: "none",
  },
}

function PrevSlideSelector({ changeSlide, numberOfArticles, current }) {
  const prev = current === 0 ? numberOfArticles - 1 : current - 1
  return (
    <button
      sx={{
        ...buttonStyles,
        boxShadow: "rgba(27, 45, 69, 0.1) 0px 5px 10px 0px",
        right: ["calc(-42.16vw + 157.85px)", "calc(-18.15vw + 138.20px)", "24px"],
      }}
      onClick={() => changeSlide(prev)}
    >
      <FiChevronLeft sx={{ fontSize: "25px" }} />
    </button>
  )
}

function NextSlideSelector({ changeSlide, numberOfArticles, current }) {
  const next = current === numberOfArticles - 1 ? 0 : current + 1
  return (
    <button
      sx={{
        ...buttonStyles,
        boxShadow: "rgba(27, 45, 69, 0.1) 0px 5px 10px 0px",
        left: ["calc(-42.16vw + 157.85px)", "calc(-18.15vw + 138.20px)", "24px"],
      }}
      onClick={() => changeSlide(next)}
    >
      <FiChevronRight sx={{ fontSize: "25px" }} />
    </button>
  )
}

function ProductPreview({
  slide: {
    productGroupName,
    products,
    productLink,
    brandName,
    alternativeProductName,
    reasons,
    mirror,
    previewImage,
  },
}) {
  const gridPosition1 = mirror === true ? "2 / span 1" : "1 / span 1"
  const gridPosition2 = mirror === true ? "1 / span 1" : "2 / span 1"
  const brand = brandName && brandName !== "" ? brandName : "sleep.ink"
  const productName =
    products.length > 1
      ? productGroupName
      : alternativeProductName && alternativeProductName !== ""
      ? alternativeProductName
      : products[0].name
  const reviewCount = products.reduce((acc, curr) => (acc += curr.reviewCount), 0)
  const reviewRating = products.reduce(
    (acc, curr) => (curr.reviewRating > acc ? curr.reviewRating : acc),
    0
  )
  // const altPosition = alternativeLeft ? "1 / span 2" : "4 / span 2"
  return (
    <Flex.column justify="center">
      <div
        sx={{
          display: "grid",
          gridTemplateColumns: [
            "1fr 1fr",
            "1fr 1fr",
            "1fr minmax(20px, 200px) minmax(600px, 900px) minmax(20px, 200px) 1fr",
          ],
        }}
      >
        <div
          sx={{
            gridColumn: ["1 / span 2", "1 / span 2", "3 / span 1"],
            display: "grid",
            gridTemplateColumns: ["1fr", "1fr", "1fr 1fr"],
            gridRow: ["1 / span 4", "1 / span 4", "1 / span 1"],
          }}
        >
          <Img
            sx={{
              height: "400px",
              gridColumn: ["1 / span 2", "1 / span 2", gridPosition1],
              gridRow: ["2 / span 1", "2 / span 1", "1 / span 2"],
              alignSelf: "center",
            }}
            fluid={getFluidFromAsset(previewImage, 600)}
            imgStyle={{ objectFit: "contain", objectPosition: "center center" }}
          />

          <div
            sx={{
              gridColumn: ["1 / span 2", "1 / span 2", gridPosition2],
              gridRow: ["1 / span 1", "1 / span 1", ""],
              textAlign: "center",
              alignItems: "center",
              display: "grid",
              maxHeight: "150px",
            }}
          >
            <h4 sx={{ variant: "text.h3", my: 2 }}>{brand}</h4>
            <h3
              sx={{
                fontFamily: "heading",
                fontSize: ["28px", "28px", "38px"],
                lineHeight: ["30px", "30px", "44px"],
                letterSpacing: "-1.24px",
                fontWeight: ["medium", "medium", "regular"],
                mt: 2,
                mb: 5,
              }}
            >
              {productName}
            </h3>
            <div sx={{ display: "flex", justifySelf: "center" }}>
              <div>{`(${reviewCount})`}</div>
              <Flex.row sx={{ ml: 2, mr: 3 }}>
                {[1, 2, 3, 4, 5].map((num, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={16}
                    color={num <= reviewRating ? "stage" : "#ddd"}
                    sx={{ mt: "4.75px" }}
                  />
                ))}
                <a
                  href="https://www.trustedshops.de/bewertung/bewerten_X171CC05C7CF0FBC03EB75B9E6E73D132.html"
                  sx={{ color: "black", mt: 1, ml: 1 }}
                >
                  Jetzt bewerten
                </a>
              </Flex.row>
            </div>
          </div>
          <div sx={{ gridColumn: ["", "", gridPosition2], display: "grid" }}>
            <Flex.column
              sx={{
                mb: 4,
                mt: 6,
                width: "90%",
                justifySelf: "center",
              }}
            >
              {reasons.map(({ title, icon }) => (
                <Flex.row align="center" sx={{ my: 4 }} key={title}>
                  <Icon name={icon} size={22} color="black" />
                  <div sx={{ ml: 3 }}>{title}</div>
                </Flex.row>
              ))}
            </Flex.column>
            <CMSLink
              sx={{
                pt: 5,
                pb: 5,
                width: "90%",
                maxHeight: "60px",
                textAlign: "center",
                bg: "almostBlack",
                color: "white",
                fontFamily: "heading",
                textTransform: "uppercase",
                justifySelf: "center",
              }}
              link={productLink}
            />
            <Flex.row
              sx={{
                justifySelf: "center",
                my: 7,
                flexWrap: "wrap",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              {"attributes" in products[0] &&
                products[0].attributes.map((attribute, i) => (
                  <Flex.row align="baseline" sx={{ mx: 3 }} key={i}>
                    <Icon name="Check" size={10} />
                    <div sx={{ ml: 2, textTransform: "uppercase", fontSize: 0 }}>{attribute}</div>
                  </Flex.row>
                ))}
            </Flex.row>
          </div>
        </div>
      </div>
    </Flex.column>
  )
}

function ProductPreviewSlider({ automatedSlideshow, slideInterval, slides }) {
  const [slideIndex, setSlideIndex] = useState(0)
  const handleSlideToShow = index => {
    setSlideIndex(index)
  }
  return (
    <SectionGrid sx={{ py: 11 }}>
      <SectionGrid.narrow>
        <Carousel
          slideIndex={slideIndex}
          autoplay={automatedSlideshow}
          autoGenerateStyleTag={false}
          autoplayInterval={1000 * slideInterval}
          speed={500}
          framePadding="0px"
          heightMode="max"
          pauseOnHover={true}
          slidesToShow={1}
          enableKeyboardControls={true}
          dragging={true}
          wrapAround={true}
          afterSlide={index => setSlideIndex(index)}
          renderCenterLeftControls={() => {
            return slides.length > 1 ? (
              <PrevSlideSelector
                changeSlide={handleSlideToShow}
                numberOfArticles={slides.length}
                current={slideIndex}
              />
            ) : null
          }}
          renderBottomCenterControls={() => null}
          renderCenterRightControls={() => {
            return slides.length > 1 ? (
              <NextSlideSelector
                changeSlide={handleSlideToShow}
                numberOfArticles={slides.length}
                current={slideIndex}
              />
            ) : null
          }}
          renderBottomLeftControls={() => null}
          width="100%"
          slideWidth={1}
          sx={{ my: "auto" }}
        >
          {slides &&
            slides.length > 0 &&
            slides.map((slide, index) => <ProductPreview slide={slide} />)}
        </Carousel>
      </SectionGrid.narrow>
    </SectionGrid>
  )
}

export { ProductPreviewSlider }
